import { CamelCaseKeys } from '@/common/types/snake-to-camel-case';

export const LEDGER_ENTRY_STATUSES = ['NOT_SETTLED', 'SETTLED'] as const;
export type LedgerEntryStatus = (typeof LEDGER_ENTRY_STATUSES)[number];

export const LEDGER_ENTRY_STATUS_LABEL: Record<LedgerEntryStatus, string> = {
	NOT_SETTLED: 'Do rozliczenia',
	SETTLED: 'Rozliczone',
};

export interface DriverLedgerCategoryOptionApiData {
	id: number;
	name: string;
}

export type DriverLedgerCategoryOption = CamelCaseKeys<DriverLedgerCategoryOptionApiData>;

export interface DriverLedgerEntryLineApiData {
	id: number;
	category_id: number | null;
	category_name: string | null;
	amount: number;
	description: string | null;
	position: number;
}

export type DriverLedgerEntryLine = CamelCaseKeys<DriverLedgerEntryLineApiData>;

export interface DriverLedgerEntryApiData {
	id: number;
	driver_id: string;
	date: string;
	amount: number;
	currency: string;
	amount_pln: number;
	exchange_rate: string | null;
	amount_eur: number;
	exchange_rate_eur: string | null;
	target_currency: string;
	category_id: number | null;
	category_name: string | null;
	description: string | null;
	payment_date: string | null;
	status: LedgerEntryStatus;
	has_original_document: boolean;
	is_mine: boolean;
	created_at: string | null;
	updated_at: string | null;
	created_by: string | null;
	updated_by: string | null;
	vehicle_id: string | null;
	vehicle_name: string | null;
	vehicle_registration_number: string | null;
	commission: number | null;
	lines: DriverLedgerEntryLineApiData[];
}

export type DriverLedgerEntry = CamelCaseKeys<DriverLedgerEntryApiData>;

export interface DriverLedgerPaginatedApiData {
	data: {
		items: DriverLedgerEntryApiData[];
		total: number;
		page: number;
		per_page: number;
		total_pages: number;
	};
}

export interface DriverLedgerPaginatedResponse {
	items: DriverLedgerEntry[];
	total: number;
	page: number;
	perPage: number;
	totalPages: number;
}

export interface DriverLedgerFilters {
	page?: number;
	perPage?: number;
	dateFrom?: string;
	dateTo?: string;
	currency?: string;
	categoryId?: number;
	status?: LedgerEntryStatus;
	settledBySalaryId?: number;
}

export interface CurrencyBalance {
	currency: string;
	balance: number;
	balancePln: number;
	balanceEur: number;
}

export interface DriverLedgerSummaryApiData {
	data: {
		currencies: { currency: string; balance: number; balance_pln: number; balance_eur: number }[];
		total_pln: number;
		total_eur: number;
	};
}

export interface DriverLedgerSummary {
	currencies: CurrencyBalance[];
	totalPln: number;
	totalEur: number;
}

export interface EntryLinePayload {
	category_id: number | null;
	amount: number;
	description: string | null;
}

export interface StoreDriverLedgerPayload {
	date: string;
	amount: number;
	currency: string;
	target_currency: 'PLN' | 'EUR';
	lines: EntryLinePayload[];
	description: string | null;
	payment_date: string | null;
	vehicle_id: string | null;
	commission: number | null;
	status: LedgerEntryStatus;
	has_original_document: boolean;
}

export interface UpdateDriverLedgerPayload {
	date?: string;
	amount?: number;
	currency?: string;
	target_currency?: 'PLN' | 'EUR';
	lines?: EntryLinePayload[];
	description?: string | null;
	payment_date?: string | null;
	vehicle_id?: string | null;
	commission?: number | null;
	status?: LedgerEntryStatus;
	has_original_document?: boolean;
}

export interface VehicleSearchResult {
	id: string;
	name: string;
	registrationNumber: string;
}

export interface DriverLedgerFormDialogData {
	driverId: string;
	entry?: DriverLedgerEntry;
}

export type DriverLedgerFormDialogResult =
	| { action: 'create'; payload: StoreDriverLedgerPayload }
	| { action: 'update'; payload: UpdateDriverLedgerPayload };
